import { Sparkles, Mic, FileText, User, Star, Building2, Upload } from 'lucide-react';
import { cn } from '@/lib/utils';

export function MockBrowserFrame({
  title,
  caption,
  children,
  className,
}: {
  title: string;
  caption?: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'rounded-xl border border-border/60 bg-card shadow-lg overflow-hidden',
        className,
      )}
      role="img"
      aria-label={caption ?? title}
    >
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-border/50 bg-muted/40">
        <span className="h-2.5 w-2.5 rounded-full bg-destructive/60" />
        <span className="h-2.5 w-2.5 rounded-full bg-amber-400/80" />
        <span className="h-2.5 w-2.5 rounded-full bg-emerald-500/70" />
        <span className="ml-2 text-xs text-muted-foreground font-medium truncate">{title}</span>
      </div>
      <div className="p-4 md:p-5 bg-gradient-to-br from-brand-surface-light to-background min-h-[200px]">
        {children}
        {caption ? (
          <p className="text-[11px] text-muted-foreground text-center pt-3 mt-3 border-t border-border/40">
            {caption}
          </p>
        ) : null}
      </div>
    </div>
  );
}

export function MockPositionsOverview({ caption }: { caption?: string }) {
  return (
    <MockBrowserFrame title="HiroMatch — Posizioni" caption={caption}>
      <div className="space-y-2">
        <div className="flex justify-between items-center">
          <span className="text-xs font-semibold">Posizioni attive</span>
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-brand-accent/15 text-brand-accent">3 aperte</span>
        </div>
        {[
          { title: 'Senior Product Designer', apps: 24, score: '82%' },
          { title: 'Account Executive B2B', apps: 41, score: '76%' },
          { title: 'DevOps Engineer', apps: 12, score: '91%' },
        ].map((row) => (
          <div
            key={row.title}
            className="flex items-center gap-3 p-2.5 rounded-lg border border-border/50 bg-background/80"
          >
            <div className="h-8 w-8 rounded-md bg-brand-accent/10 flex items-center justify-center shrink-0">
              <BriefcaseIcon />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium truncate">{row.title}</p>
              <p className="text-[10px] text-muted-foreground">{row.apps} candidature</p>
            </div>
            <span className="text-[10px] font-semibold text-emerald-600">{row.score}</span>
          </div>
        ))}
      </div>
    </MockBrowserFrame>
  );
}

function BriefcaseIcon() {
  return (
    <svg className="h-4 w-4 text-brand-accent" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="2" y="7" width="20" height="14" rx="2" />
      <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" />
    </svg>
  );
}

export function MockJobPositionAI({ caption }: { caption?: string }) {
  return (
    <MockBrowserFrame title="Posizione — AutoFill JD" caption={caption}>
      <div className="grid md:grid-cols-5 gap-3">
        <div className="md:col-span-3 space-y-2">
          <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Descrizione ruolo</p>
          <div className="rounded-lg border border-border/60 p-2.5 text-[11px] leading-relaxed bg-background space-y-1">
            <p className="font-medium">Senior Product Designer — Milano (ibrido)</p>
            <p className="text-muted-foreground">
              Cerchiamo un designer che unisca UX research e design system. Esperienza SaaS B2B…
            </p>
          </div>
          <div className="flex gap-2">
            <span className="text-[10px] px-2 py-1 rounded-md bg-muted">Senior</span>
            <span className="text-[10px] px-2 py-1 rounded-md bg-muted">Full-time</span>
          </div>
        </div>
        <div className="md:col-span-2 rounded-lg border border-brand-accent/30 bg-brand-accent/5 p-2.5 space-y-2">
          <div className="flex items-center gap-1.5 text-brand-accent">
            <Sparkles className="h-3.5 w-3.5" />
            <span className="text-[10px] font-semibold">Assistente AI</span>
          </div>
          <p className="text-[10px] text-muted-foreground leading-snug">
            Ho arricchito requisiti e benefit in italiano. Vuoi tono più formale?
          </p>
          <div className="flex flex-wrap gap-1">
            {['Aggiungi KPI', 'EU AI Act note', 'Traduci EN'].map((chip) => (
              <span key={chip} className="text-[9px] px-1.5 py-0.5 rounded bg-background border border-border/60">
                {chip}
              </span>
            ))}
          </div>
        </div>
      </div>
    </MockBrowserFrame>
  );
}

export function MockApplicationsScores({ caption }: { caption?: string }) {
  return (
    <MockBrowserFrame title="Candidature" caption={caption}>
      <div className="space-y-1.5">
        {[
          { name: 'Laura B.', role: 'Product Designer', fit: 88, status: 'Screening OK' },
          { name: 'Marco R.', role: 'UX Researcher', fit: 72, status: 'In colloquio' },
          { name: 'Sara K.', role: 'UI Designer', fit: 65, status: 'Nuova' },
        ].map((c) => (
          <div
            key={c.name}
            className="flex items-center gap-2 p-2 rounded-lg border border-border/50 bg-background/90 text-[11px]"
          >
            <div className="h-7 w-7 rounded-full bg-muted flex items-center justify-center shrink-0">
              <User className="h-3.5 w-3.5 text-muted-foreground" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium truncate">{c.name}</p>
              <p className="text-[10px] text-muted-foreground truncate">{c.role}</p>
            </div>
            <div className="text-right shrink-0">
              <p className="font-bold text-brand-accent">{c.fit}%</p>
              <p className="text-[9px] text-muted-foreground">{c.status}</p>
            </div>
          </div>
        ))}
      </div>
    </MockBrowserFrame>
  );
}

export function MockInterviewReport({ caption }: { caption?: string }) {
  return (
    <MockBrowserFrame title="Report colloquio" caption={caption}>
      <div className="grid md:grid-cols-2 gap-3">
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Star className="h-4 w-4 text-amber-500 fill-amber-500" />
            <span className="text-xs font-semibold">Fit complessivo: 84/100</span>
          </div>
          <p className="text-[10px] text-muted-foreground leading-relaxed">
            Forte su design system e stakeholder management. Da approfondire: metriche di impatto su
            conversione.
          </p>
          <div className="h-1.5 rounded-full bg-muted overflow-hidden">
            <div className="h-full w-[84%] bg-brand-accent rounded-full" />
          </div>
        </div>
        <div className="rounded-lg border border-border/60 p-2 bg-muted/30 space-y-1">
          <div className="flex items-center gap-1.5 text-[10px] font-medium">
            <Mic className="h-3 w-3 text-brand-teal" />
            Trascrizione (estratto)
          </div>
          <p className="text-[9px] text-muted-foreground italic leading-snug">
            &quot;Nel mio ultimo ruolo ho ridotto il time-to-hire del 30% coordinando hiring manager e
            people ops…&quot;
          </p>
          <FileText className="h-3 w-3 text-muted-foreground" />
        </div>
      </div>
    </MockBrowserFrame>
  );
}

export function MockInterviewQuestions({ caption }: { caption?: string }) {
  return (
    <MockBrowserFrame title="Domande suggerite" caption={caption}>
      <div className="flex items-center gap-1.5 mb-2 text-brand-accent">
        <Sparkles className="h-3.5 w-3.5" />
        <span className="text-[10px] font-semibold">Suggerite per Laura B.</span>
      </div>
      <ol className="space-y-1.5 list-decimal list-inside text-[10px] text-muted-foreground">
        {[
          'Racconta un design system che hai scalato su più squadre.',
          'Come misuri l’impatto UX su KPI di prodotto?',
          'Esempio di trade-off tra velocità di delivery e qualità UX.',
        ].map((q) => (
          <li key={q} className="leading-snug bg-background/80 rounded px-2 py-1 border border-border/40">
            {q}
          </li>
        ))}
      </ol>
    </MockBrowserFrame>
  );
}

export function MockBrandedCareerPage({ caption }: { caption?: string }) {
  return (
    <MockBrowserFrame title="hiromatch.com / la-tua-azienda / …" caption={caption}>
      <div className="rounded-lg border-2 border-dashed border-brand-accent/25 p-4 text-center space-y-3 bg-white/80">
        <div className="flex justify-center">
          <div className="h-10 w-10 rounded-xl bg-brand-primary/10 flex items-center justify-center">
            <Building2 className="h-5 w-5 text-brand-primary" />
          </div>
        </div>
        <p className="text-sm font-display font-bold">La Tua Azienda S.r.l.</p>
        <p className="text-xs font-medium">Senior Product Designer</p>
        <p className="text-[10px] text-muted-foreground max-w-xs mx-auto">
          Candidati in pochi minuti — CV e contatti raccolti in modo sicuro.
        </p>
        <div className="flex flex-col gap-1.5 max-w-[200px] mx-auto text-left">
          <div className="h-7 rounded border border-border/60 bg-muted/20" />
          <div className="h-7 rounded border border-border/60 bg-muted/20" />
          <div className="h-16 rounded border border-dashed border-border flex items-center justify-center gap-1 text-[10px] text-muted-foreground">
            <Upload className="h-3 w-3" /> Carica CV
          </div>
        </div>
        <span className="inline-block text-[10px] px-3 py-1.5 rounded-md bg-brand-accent text-white font-medium">
          Invia candidatura
        </span>
      </div>
    </MockBrowserFrame>
  );
}

export type ProductMockId =
  | 'positions'
  | 'detail'
  | 'applications'
  | 'report'
  | 'career'
  | 'questions';

export function MarketingProductMockById({
  id,
  caption,
}: {
  id: ProductMockId;
  caption?: string;
}) {
  switch (id) {
    case 'positions':
      return <MockPositionsOverview caption={caption} />;
    case 'detail':
      return <MockJobPositionAI caption={caption} />;
    case 'applications':
      return <MockApplicationsScores caption={caption} />;
    case 'report':
      return <MockInterviewReport caption={caption} />;
    case 'career':
      return <MockBrandedCareerPage caption={caption} />;
    case 'questions':
      return <MockInterviewQuestions caption={caption} />;
    default:
      return <MockPositionsOverview caption={caption} />;
  }
}
